import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IOrganization, IUser } from "../../../model/model";
import axios from "axios";
import apiUrls from "../../../api";
import { storeAuthInLocalStorage } from "../../../common/util/localStorage";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import { ZendeskAPI } from "react-zendesk";
import { clearProfile } from "../../profile/state/profileSlice";
import type { IProjectGroupWithProjects } from "../../projectGroups/state/projectGroupsSlice";
import {
  clearProjectGroups,
  setProjectGroupsWithProjects,
} from "../../projectGroups/state/projectGroupsSlice";
import { clearProjects } from "../../graph/state/projectsSliceLegacy";
import { clearGallery } from "../../gallery/state/gallerySlice";
import { clearTeam } from "../../team/state/teamSlice";
import { clearAlerts } from "../../alert/state/alertsSlice";
import type { AppDispatch, RootState } from "src/common/state/store";
import { destroyHelpDocsWidget } from "src/common/util/helpDocsLighthouse/destroyHelpDocsWidget";
import { clearDeviceInstallerState } from "../../deviceInstaller/state/deviceInstallerSlice";
import { clearBuilds } from "../../builds/state/buildsSlice";

interface ILegals {
  tos: { s3_url: string };
  privacyPolicy: { s3_url: string };
}

export interface IAuthFormData {
  email: string;
  password: string;
  from_environment?: string;
  rememberme: boolean;
}

export type MfaChallengeRequirement = "mfa_required" | "mfa_enrollment_required";

export interface IMfaChallenge {
  token: string;
  method: "totp" | "email" | null;
  requirement: MfaChallengeRequirement;
  sentTo: string | null;
}

export interface IForgottenPasswordFormData {
  email: string;
}

export interface IResetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

export interface ISetPasswordFormData {
  password: string;
  passwordConfirmation: string;
}

interface IAuth {
  jwt: string;
  helpDocsJwt: string;
  userId?: number;
  currentCommitHash: string;
  legals: ILegals;
  mfaChallenge: IMfaChallenge | null;
}

const initialState: IAuth = {
  jwt: "",
  helpDocsJwt: "",
  currentCommitHash: "",
  legals: {
    tos: {
      s3_url: "",
    },
    privacyPolicy: {
      s3_url: "",
    },
  },
  mfaChallenge: null,
};

export const signIn = createAsyncThunk(
  "auth/login",
  async (
    { email, password, rememberme, from_environment = "WebApp" }: IAuthFormData & IAppLoaderAction,
    { dispatch },
  ) => {
    const response = await axios.post<{
      jwt: string | null;
      helpDocsJwt?: string;
      user: IUser | null;
      firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
      status?: { error: string | null };
      mfa_token?: string;
      mfa_method?: "totp" | "email" | null;
      mfa_sent_to?: string | null;
    }>(
      `${apiUrls.auth.signIn}?populate=*&populate[organization][populate][admin]=*&populate[organization][populate][source_logo]=*`,
      {
        identifier: email,
        password,
        from_environment,
        remember_me: rememberme,
      },
    );

    const {
      status,
      mfa_token: mfaToken,
      mfa_method: mfaMethod,
      mfa_sent_to: mfaSentTo,
    } = response.data;

    const requirement = status?.error;
    if (requirement === "mfa_required" || requirement === "mfa_enrollment_required") {
      const mfaChallenge: IMfaChallenge = {
        token: mfaToken as string,
        method: mfaMethod ?? null,
        sentTo: mfaSentTo ?? null,
        requirement,
      };
      return {
        jwt: null,
        helpDocsJwt: null,
        mfaChallenge,
      };
    }

    const { jwt, helpDocsJwt, user, firstTwoProjectGroupsWithProjects } = response.data;
    const organization = (user as IUser).organization;

    if (
      Array.isArray(firstTwoProjectGroupsWithProjects) &&
      firstTwoProjectGroupsWithProjects.length > 0
    ) {
      dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
    }
    await storeAuthInLocalStorage(
      user as IUser,
      organization,
      jwt as string,
      helpDocsJwt as string,
      true,
    );

    return { jwt, helpDocsJwt, mfaChallenge: null };
  },
);

export const verifyMfa = createAsyncThunk(
  "auth/verifyMfa",
  async (
    {
      mfaToken,
      otp,
      recoveryCode,
    }: { mfaToken: string; otp?: string; recoveryCode?: string } & IAppLoaderAction,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{
        jwt: string;
        helpDocsJwt: string;
        user: IUser;
        firstTwoProjectGroupsWithProjects?: IProjectGroupWithProjects[];
        recovery_codes_remaining?: number;
      }>(
        apiUrls.mfa.verify,
        { mfa_token: mfaToken, otp, recovery_code: recoveryCode },
        {
          headers: { Authorization: undefined },
        },
      );

      const {
        jwt,
        helpDocsJwt,
        user,
        firstTwoProjectGroupsWithProjects,
        recovery_codes_remaining: recoveryCodesRemaining,
      } = response.data;
      const organization = user.organization;

      if (
        Array.isArray(firstTwoProjectGroupsWithProjects) &&
        firstTwoProjectGroupsWithProjects.length > 0
      ) {
        dispatch(setProjectGroupsWithProjects(firstTwoProjectGroupsWithProjects));
      }
      await storeAuthInLocalStorage(user, organization, jwt, helpDocsJwt, true);

      return { jwt, helpDocsJwt, recoveryCodesRemaining: recoveryCodesRemaining ?? null };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue({
          ...(err.response?.data as Record<string, unknown> | undefined),
          status: err.response?.status ?? null,
        });
      }
      throw err;
    }
  },
);

export const resendMfaChallenge = createAsyncThunk(
  "auth/resendMfaChallenge",
  async ({ mfaToken }: { mfaToken: string } & IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ ok: boolean }>(
        apiUrls.mfa.challenge,
        { mfa_token: mfaToken },
        { headers: { Authorization: undefined } },
      );
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const getEditorWebLastCommit = createAsyncThunk("auth/getEditorWebLastCommit", async () => {
  const response = await axios.get<unknown>(apiUrls.getEditorWebLastCommit);
  return { legals: response.data };
});

export const fetchLegals = createAsyncThunk(
  "auth/fetchLegals",
  async (_: IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.get<ILegals>(apiUrls.legals);
      return { legals: response.data };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      }
      throw err;
    }
  },
);

export const forgottenPassword = createAsyncThunk(
  "auth/forgottenPassword",
  async ({ email }: IForgottenPasswordFormData & IAppLoaderAction, { rejectWithValue }) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.forgetPassword, {
        email,
      });
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: IResetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(apiUrls.auth.resetPassword, {
        code: code,
        password: password,
        passwordConfirmation: passwordConfirmation,
      });
      return { response };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(err?.response?.data);
      } else {
        throw err;
      }
    }
  },
);

export const setInitialPassword = createAsyncThunk(
  "auth/setInitialPassword",
  async (
    {
      password,
      passwordConfirmation,
      code,
    }: ISetPasswordFormData & { code: string } & IAppLoaderAction,
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post<{ jwt: string; user: IUser }>(
        `${apiUrls.base}/api/auth/set-initial-password`,
        {
          data: { code, password, passwordConfirmation },
        },
        {
          headers: {
            Authorization: undefined,
          },
        },
      );
      return { response };
    } catch (err) {
      if (axios.isAxiosError<{ error?: { message?: string } }>(err)) {
        const errorMessage = err?.response?.data?.error?.message || "An error occurred";

        const errorMap: { [key: string]: string } = {
          "Missing required fields": "pages.setPassword.errors.missingFields",
          "Passwords do not match": "pages.setPassword.errors.passwordsDoNotMatch",
          "Password requirements not met": "pages.setPassword.errors.passwordRequirementsNotMet",
          "Invalid or already used token": "pages.setPassword.errors.invalidOrUsedToken",
        };

        const translationKey = errorMap[errorMessage] || "pages.setPassword.errors.genericError";

        return rejectWithValue({
          error: {
            message: translationKey,
          },
        });
      } else {
        throw err;
      }
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuth: (
      state: IAuth,
      action: PayloadAction<{ jwt: string; user: IUser; organization: IOrganization }>,
    ) => {
      state.jwt = action.payload.jwt;
    },
    resetJwt: (state: IAuth) => {
      state.jwt = "";
      state.mfaChallenge = null;
      axios.defaults.headers.common["Authorization"] = undefined;
      window.localStorage.clear();
    },
    setCurrentCommitHash: (state: IAuth, action: PayloadAction<string>) => {
      state.currentCommitHash = action.payload;
    },
    clearMfaChallenge: (state: IAuth) => {
      state.mfaChallenge = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.fulfilled, (state, { payload }) => {
      if (payload.mfaChallenge) {
        state.mfaChallenge = payload.mfaChallenge;
        return;
      }
      state.jwt = payload.jwt as string;
      state.helpDocsJwt = payload.helpDocsJwt as string;
      state.mfaChallenge = null;
    });
    builder.addCase(verifyMfa.fulfilled, (state, { payload }) => {
      state.jwt = payload.jwt;
      state.helpDocsJwt = payload.helpDocsJwt;
      state.mfaChallenge = null;
    });
    builder.addCase(fetchLegals.fulfilled, (state, { payload }) => {
      state.legals = payload.legals;
    });
  },
});

export const authReducer = authSlice.reducer;

export const getHelpDocsJwt = (state: RootState) => {
  return state.auth.helpDocsJwt;
};

export const getLegals = (state: IAuth) => state.legals;

export const logoutAuth = () => (dispatch: AppDispatch) => {
  ZendeskAPI("webWidget", "hide");
  dispatch(clearProfile());
  dispatch(clearProjectGroups());
  dispatch(clearProjects());
  dispatch(clearGallery());
  dispatch(clearTeam());
  dispatch(clearAlerts());
  dispatch(clearDeviceInstallerState());
  dispatch(clearBuilds());
  const CURRENT_COMMIT_HASH = localStorage.getItem("CURRENT_COMMIT_HASH");
  dispatch(authSlice.actions.resetJwt());
  if (CURRENT_COMMIT_HASH) {
    localStorage.setItem("CURRENT_COMMIT_HASH", CURRENT_COMMIT_HASH);
  }

  destroyHelpDocsWidget();
};

export const { setAuth, clearMfaChallenge } = authSlice.actions;
